import { ResourcesLike, findAndMap } from "~/utils";
import { Choice, NewChoice } from "../Choice";
import { cardsDefRepository } from "../card/cardsDefRepository";
import { Dreadnought, FactionType, ResourceType, UnitType } from "../constant";
import { AgentEffectCommands, AgentEffectDescriptionRaw } from "./AgentEffect";
import { AgentEffectFunction } from "./AgentEffectFunction";
import { sellSpiceAmounts } from "./sellSpiceAmounts";

export const AgentEffectChoices = {
  opponentsDiscard({ playerId, cardId }: { playerId: string; cardId: string }) {
    return new AgentEffectFunction(({ game }) => {
      const { hand, discard } = game.getPlayer(playerId);
      const card = hand.getById(cardId);
      hand.move(discard, card);
      return true;
    });
  },
  canRetreat({ troopCount }: { troopCount: number }) {
    return new AgentEffectFunction(({ player }) => {
      const { combatTroops, garnisonTroops } = player;
      combatTroops.moveCount(garnisonTroops, troopCount);
      return true;
    });
  },
  canDeployToConflict({
    troopCount,
    type,
    nextUnitType,
    maxUnitCount,
  }: {
    troopCount: number;
    type: UnitType;
    nextUnitType?: UnitType;
    maxUnitCount?: number;
  }) {
    return new AgentEffectFunction(({ player, game }) => {
      const garnison = player.getGarnison(type);
      const combatPile = player.getCombatPile(type);
      garnison.moveCount(combatPile, troopCount);
      if (nextUnitType && maxUnitCount && maxUnitCount > troopCount) {
        game.executor.add(
          AgentEffectCommands.canDeployToConflict({
            amount: maxUnitCount - troopCount,
            type: nextUnitType,
          }),
          { playerId: player.id }
        );
      }
      return true;
    });
  },
  payResource({ willPay, cost }: { willPay: boolean; cost: ResourcesLike }) {
    return new AgentEffectFunction(({ player }) => {
      if (willPay) {
        player.resources.pay(cost);
      }
      return true;
    });
  },
  chooseFactionChange({
    amount,
    faction,
  }: {
    faction: FactionType;
    amount: number;
  }) {
    return new AgentEffectFunction(({ player, game }) => {
      game.factionLevelChange(faction, player, amount);
      return true;
    });
  },
  trashCard({ cardId }: { cardId: string }) {
    return new AgentEffectFunction(({ player, game }) => {
      const { hand, discard, playZone, revealZone, trashZone, deck } = player;
      const zones = [hand, discard, playZone, revealZone, deck];
      zones.forEach((zone) => {
        if (zone.pile.map((card) => card.id).includes(cardId)) {
          const card = zone.getById(cardId);
          zone.move(trashZone, card);
          const { hasUnload, revealEffects } = card.getDef(cardsDefRepository);
          if (hasUnload) {
            game.executor.addAllInReverse(revealEffects, {
              playerId: player.id,
              cardId: card.id,
            });
          }
        }
      });
      return true;
    });
  },
  trashMe({ willTrash, cardId }: { willTrash: true; cardId: string }) {
    return new AgentEffectFunction(({ player }) => {
      if (willTrash) {
        const card = player.playZone.getById(cardId);
        player.playZone.move(player.trashZone, card);
      }
      return true;
    });
  },
  drawBenegesseritCardFromDiscard({ cardId }: { cardId: string }) {
    return new AgentEffectFunction(({ player }) => {
      const card = player.discard.getById(cardId);
      const cardDef = cardsDefRepository.getDef(card);
      if (!cardDef.factions.includes(FactionType.BeneGesserit)) {
        throw new Error("unexpected");
      }
      player.discard.move(player.hand, card);
      return true;
    });
  },
  replaceOpponentConflictTroop({ opponentId }: { opponentId: string }) {
    return new AgentEffectFunction(({ player, game }) => {
      const opponent = game.getPlayer(opponentId);
      if (!opponent.combatTroops.length) {
        throw new Error(`expected combat troops for player ${opponent.id}`);
      }
      if (!player.orbitTroops.length) {
        throw new Error(`expected orbit troops for player ${player.id}`);
      }
      opponent.combatTroops.moveCount(opponent.orbitTroops, 1);
      player.orbitTroops.moveCount(player.combatTroops, 1);
      return true;
    });
  },
  sellSpice({ spiceAmount }: { spiceAmount: number }) {
    return new AgentEffectFunction(({ player }) => {
      const conversion = sellSpiceAmounts.find(
        ({ spice }) => spiceAmount === spice
      );
      if (!conversion) {
        throw new Error("could not find sold spice amount");
      }
      const { spice, solaris } = conversion;
      player.resources.pay({ [ResourceType.Spice]: spice });
      player.resources.add({ [ResourceType.Solaris]: solaris });
      return true;
    });
  },
  canPassTurn({ passTurn }: { passTurn: boolean }) {
    return new AgentEffectFunction(({ game, player }) => {
      if (passTurn) {
        game.executor.add(AgentEffectCommands.endPlayerTurn, {
          playerId: player.id,
        });
      }
      return true;
    });
  },
  chooseLooseConflictTroops({
    looseConflictTroops,
  }: {
    looseConflictTroops: number;
  }) {
    return new AgentEffectFunction(({ player }) => {
      const { combatTroops, orbitTroops } = player;
      if (combatTroops.length < looseConflictTroops) {
        throw new Error(
          `unexpected: not enough conflict troops: expected at least ${looseConflictTroops}, got ${combatTroops.length}`
        );
      }
      combatTroops.pile.slice(0, looseConflictTroops).forEach((troop) => {
        combatTroops.move(orbitTroops, troop);
      });
      return true;
    });
  },
  recallAgent({ slotId }: { slotId: string }) {
    return new AgentEffectFunction(({ player, game }) => {
      const actionSlot = game.getActionSlot(slotId);
      const meaple = actionSlot.meaples.pile.find(
        (meaple) => meaple.owner === player.id
      );
      if (!meaple) {
        throw new Error(`unexpected. expected a player agent on action slot`);
      }
      actionSlot.meaples.move(player.workerPile, meaple);
      return true;
    });
  },
  opponentDiscardOrLooseTroop(
    command: { looseTroop: number } | { cardId: string }
  ) {
    return new AgentEffectFunction(({ player }) => {
      const { hand, combatTroops, discard } = player;
      if ("cardId" in command) {
        const card = hand.getById(command.cardId);
        hand.move(discard, card);
      } else {
        combatTroops.removeCount(command.looseTroop);
      }
      return true;
    });
  },
  reserveShopCardWithDiscount({
    cardId,
    discount,
  }: {
    cardId: string;
    discount: number;
  }) {
    return new AgentEffectFunction(({ player, game }) => {
      game.reserveCard(player.id, cardId, discount);
      return true;
    });
  },
  drawOrTrash({
    cardId,
    action,
  }: {
    cardId: string;
    type: "drawOrTrash";
    action: "draw" | "trash";
  }) {
    return new AgentEffectFunction((args) => {
      const { player } = args;
      const { deck, hand } = player;
      const card = deck.getById(cardId);
      if (action === "draw") {
        deck.move(hand, card);
      } else {
        this.trashCard({ cardId: card.id }).apply(args);
      }
      return true;
    });
  },
  orChoice({ effect }: { effect: AgentEffectDescriptionRaw }) {
    return new AgentEffectFunction(() => {
      return { effect };
    });
  },
  buyOnDeck({ cardId, buyOnDeck }: { cardId: string; buyOnDeck: boolean }) {
    return new AgentEffectFunction(({ player }) => {
      const { discard, deck } = player;
      const card = player.getCard(cardId);
      if (buyOnDeck) {
        discard.move(deck, card);
      }
      return true;
    });
  },
  freeCard({ cardId, onTop = false }: { cardId: string; onTop?: boolean }) {
    return new AgentEffectFunction(({ player, game }) => {
      game.acquireCard(player.id, cardId, onTop);
      return true;
    });
  },
  blockActionSlot({ slotId }: { slotId: string }) {
    return new AgentEffectFunction(({ player, game }) => {
      const actionSlot = game.getActionSlot(slotId);
      const { blockedBy = [] } = actionSlot;
      blockedBy.push(player.id);
      actionSlot.blockedBy = blockedBy;
      return true;
    });
  },
  discardCard({ cardId }: { cardId: string }) {
    return new AgentEffectFunction(({ player, game }) => {
      const { hand, discard } = player;
      const card = hand.getById(cardId);
      hand.move(discard, card);
      const { hasUnload, revealEffects } = card.getDef(cardsDefRepository);
      if (hasUnload) {
        game.executor.addAllInReverse(revealEffects, {
          playerId: player.id,
          cardId: card.id,
        });
      }
      return true;
    });
  },
  freighterChoice({ freighter }: { freighter: "up" | "recall" }) {
    return new AgentEffectFunction(({ player, game }) => {
      const { executor } = game;
      if (freighter === "up") {
        executor.add(AgentEffectCommands.freighterUp, { playerId: player.id });
      } else if (freighter === "recall") {
        executor.add(AgentEffectCommands.recallFreighter, {
          playerId: player.id,
        });
      }
      return true;
    });
  },
  discardIntrigueCard({ intrigueCardId }: { intrigueCardId: string }) {
    return new AgentEffectFunction(({ player, game }) => {
      const { intrigueCards } = player;
      const card = intrigueCards.getById(intrigueCardId);
      intrigueCards.remove(card);
      game.intrigueDeck.pile = [card, ...game.intrigueDeck.pile];
      return true;
    });
  },
  playAgain({ playAgain }: { playAgain: boolean }) {
    return new AgentEffectFunction(({ player, game }) => {
      if (!playAgain) return true;
      const { executor } = game;
      executor.addInHead(AgentEffectCommands.setTurnToPlayer(player.id), {
        playerId: player.id,
      });
      return true;
    });
  },
  dreadnoughtControlSlot({ slotId }: { slotId: string }) {
    return new AgentEffectFunction(({ player, game }) => {
      const { combatDreadnought } = player;
      const dreadnought = combatDreadnought.last();
      if (!dreadnought) {
        throw new Error("unexpected: no combat dreadnought");
      }
      const slot = game.getActionSlot(slotId);
      const { controlDreadnought } = slot;
      if (controlDreadnought) {
        throw new Error("unexpected: slot contains a dreadnought");
      }
      const tempArr: Dreadnought[] = [];
      combatDreadnought.move(tempArr, dreadnought);
      slot.controlDreadnought = tempArr[0];
      return true;
    });
  },
  acquireTechTile({
    techTileId,
    discount = 0,
    mayBuyWithSolaris = false,
  }: {
    techTileId: string;
    discount?: number;
    mayBuyWithSolaris?: boolean;
  }) {
    return new AgentEffectFunction(({ player, game }) => {
      const boughtTile = game.getTechTile(techTileId);
      if (!boughtTile) {
        throw new Error(
          `unexpected: could not find tech tile with id=${techTileId}`
        );
      }
      const { techNegociators, resources, orbitTroops } = player;
      const { cost, buyEffects = [] } = boughtTile;
      const costBeforeNegociators = Math.max(0, cost - discount);
      const finalCost = Math.max(
        0,
        costBeforeNegociators - techNegociators.length
      );
      const usedNegociatorsCount = costBeforeNegociators - finalCost;
      const solarisCost = {
        [ResourceType.Solaris]: finalCost,
      };
      const spiceCost = {
        [ResourceType.Spice]: finalCost,
      };

      if (mayBuyWithSolaris && resources.canPay(solarisCost)) {
        if (resources.canPay(spiceCost)) {
          game.executor.add(
            AgentEffectCommands.choosePaymentForTechTile({
              cost: finalCost,
            }),
            { playerId: player.id }
          );
        } else {
          resources.pay(solarisCost);
        }
      } else if (resources.canPay(spiceCost)) {
        resources.pay(spiceCost);
      } else {
        throw new Error("unexpected: cannot pay for tech tile");
      }

      const { techTiles } = game;
      const { item: pile } =
        findAndMap(techTiles, (pile) => pile.findById(techTileId)) ?? {};
      if (!pile) {
        throw new Error("unexptected: tech tile pile not found");
      }
      pile.move(player.techTiles, boughtTile);
      techNegociators.moveCount(orbitTroops, usedNegociatorsCount);
      if (buyEffects.length) {
        game.executor.addAllInReverse(buyEffects, { playerId: player.id });
      }

      return true;
    });
  },
  choosePaymentForTechTile({
    cost,
    resourceType,
  }: {
    cost: number;
    resourceType: ResourceType;
  }) {
    return new AgentEffectFunction(({ player }) => {
      const { resources } = player;
      resources.pay({
        [resourceType]: cost,
      });
      return true;
    });
  },
  ennemyRetreatNUnit({
    opponentId,
    unitCount,
    maxUnitCount,
  }: {
    opponentId: string;
    unitCount: number;
    maxUnitCount: number;
    type: "ennemyRetreatNUnit";
  }) {
    return new AgentEffectFunction(({ game, player, card, slot }) => {
      if (unitCount > maxUnitCount) {
        throw new Error("unexpected: unitCount > maxUnitCount");
      }
      game.forceRetreatNUnit(opponentId, unitCount);
      const unitLeft = maxUnitCount - unitCount;
      const opponentsWithUnits = game
        .getOpponents(player.id)
        .filter(
          (opponent) =>
            opponent.combatDreadnought.length || opponent.combatTroops.length
        );
      const nextOpponent = game.getNextPlayerIn(
        opponentsWithUnits,
        opponentId,
        false
      );
      if (nextOpponent && unitLeft) {
        game.executor.add(
          AgentEffectCommands.ennemyRetreatNUnit({
            unitCount: unitLeft,
            opponentId: nextOpponent.id,
          }),
          { playerId: player.id, cardId: card?.id, actionSlotId: slot?.id }
        );
      }
      return true;
    });
  },
  intrigueScry({ intrigueCardId }: { intrigueCardId: string }) {
    return new AgentEffectFunction(({ game, player }) => {
      const { intrigueDeck } = game;
      const drawnCard = intrigueDeck.getById(intrigueCardId);
      intrigueDeck.move(player.intrigueCards, drawnCard);
      return true;
    });
  },
};

export type EffectChoice = Choice<typeof AgentEffectChoices>;
export type EffectChoiceType = EffectChoice["type"];
export type EffectChoiceResult = EffectChoice["results"][0];
export type EffectChoiceOption<
  Key extends EffectChoiceType = keyof typeof AgentEffectChoices
> = Choice<typeof AgentEffectChoices, Key>["possibleChoices"][0];
export type AgentEffectNewChoice = NewChoice<typeof AgentEffectChoices>;

export type AgentEffectResult =
  | boolean
  | AgentEffectNewChoice[]
  | { effect: AgentEffectDescriptionRaw };

export const AgentEffectResult = {
  isBoolean(x: AgentEffectResult): x is boolean {
    return typeof x === "boolean";
  },
  isChoice(x: AgentEffectResult): x is AgentEffectNewChoice[] {
    return !AgentEffectResult.isBoolean(x) && Array.isArray(x);
  },
  isEffect(x: AgentEffectResult): x is { effect: AgentEffectDescriptionRaw } {
    return !AgentEffectResult.isBoolean(x) && "effect" in x;
  },
};
